/**
 * Klein-Fenster zum direkten Bearbeiten an der Stelle, an der ein Wert steht (Antoine 28.09.2026:
 * „Diese sollten in den Kacheln direkt editierbar sein“ + „Das betrifft alle Kacheln, wo es Sinn
 * macht“ — Kopf-Kacheln, Eckdaten- und Kunden-Karte). Muster wie das Kalenderblatt: Klick öffnet
 * darunter ein kleines Fenster nur mit den Feldern dieser Kachel. „Übernehmen“ oder Enter speichert,
 * Escape / Klick daneben bricht ab. Fehler stehen mit der Ursache im Fenster (nicht generisch).
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Button, DateInput, Icon, Input, Select, ZeitEingabe } from "../components";

export function KleinFenster({
  ausloeser,
  children,
  testId,
  className = "",
}: {
  ausloeser: (a: { offen: boolean; umschalten: () => void }) => ReactNode;
  children: (schliessen: () => void) => ReactNode;
  testId?: string;
  className?: string;
}) {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!offen) return;
    const weg = (e: MouseEvent) => {
      const ziel = e.target as HTMLElement;
      // Radix-Auswahllisten liegen im Portal außerhalb des Fensters — kein „daneben“.
      if (ref.current?.contains(ziel) || ziel.closest?.("[data-radix-popper-content-wrapper]")) return;
      setOffen(false);
    };
    const taste = (e: KeyboardEvent) => e.key === "Escape" && setOffen(false);
    document.addEventListener("mousedown", weg);
    document.addEventListener("keydown", taste);
    return () => {
      document.removeEventListener("mousedown", weg);
      document.removeEventListener("keydown", taste);
    };
  }, [offen]);
  return (
    <div ref={ref} className={`relative min-w-0 ${className}`}>
      {ausloeser({ offen, umschalten: () => setOffen((v) => !v) })}
      {offen && (
        <div
          data-testid={testId ? `${testId}-fenster` : undefined}
          className="absolute left-0 top-full z-30 mt-2 w-72 max-w-[calc(100vw-32px)] rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-3 text-left shadow-2xl dark:border-outline/30 dark:bg-night-card"
        >
          {children(() => setOffen(false))}
        </div>
      )}
    </div>
  );
}

/** Stift, der beim Hinzeigen erscheint (Auslöser braucht die Klasse `group`). */
export function StiftHinweis() {
  return <Icon name="edit" className="ml-auto shrink-0 pl-1 text-[15px] text-secondary-fixed-dim opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />;
}

const LABEL = "block font-body-sm text-[12px] font-bold text-secondary dark:text-secondary-fixed-dim";

/** Formular-Rahmen: Übernehmen/Abbrechen, Fehler mit Ursache. `speichern` wirft bei Fehler. */
function FensterFormular({ speichern, schliessen, children }: { speichern: (fd: FormData) => Promise<void>; schliessen: () => void; children: ReactNode }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  async function absenden(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setFehler(null);
    try {
      await speichern(new FormData(e.currentTarget));
      schliessen();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={(e) => void absenden(e)} className="space-y-2.5">
      {children}
      {fehler && <p className="text-[12px] font-semibold text-error" role="alert">{fehler}</p>}
      <div className="flex justify-end gap-2 pt-0.5">
        <Button type="button" variant="ghost" className="!py-1.5 !text-[12px]" onClick={schliessen}>
          Abbrechen
        </Button>
        <Button type="submit" className="!py-1.5 !text-[12px]" disabled={busy} data-testid="klein-fenster-uebernehmen">
          {busy ? "Speichert …" : "Übernehmen"}
        </Button>
      </div>
    </form>
  );
}

type Speichern = (patch: Record<string, unknown>) => Promise<void>;

export function UhrzeitFenster({ von, bis, speichern, schliessen }: { von: string | null; bis: string | null; speichern: Speichern; schliessen: () => void }) {
  return (
    <FensterFormular
      schliessen={schliessen}
      speichern={async (fd) => {
        const v = String(fd.get("von") ?? "").trim();
        const b = String(fd.get("bis") ?? "").trim();
        await speichern({ timeFrom: v || null, timeTo: v ? b || null : null });
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <label className={LABEL}>
          Von
          <span className="mt-1 block"><ZeitEingabe name="von" defaultValue={von?.slice(0, 5) ?? ""} /></span>
        </label>
        <label className={LABEL}>
          Bis
          <span className="mt-1 block"><ZeitEingabe name="bis" defaultValue={bis?.slice(0, 5) ?? ""} /></span>
        </label>
      </div>
      <p className="text-[11px] text-secondary-fixed-dim">„Bis“ leer lassen = Ende offen.</p>
    </FensterFormular>
  );
}

export function GaesteFenster({ gaeste, speichern, schliessen }: { gaeste: number | null; speichern: Speichern; schliessen: () => void }) {
  return (
    <FensterFormular
      schliessen={schliessen}
      speichern={async (fd) => {
        const roh = String(fd.get("gaeste") ?? "").trim();
        const n = roh ? Number(roh) : null;
        if (n != null && (!Number.isInteger(n) || n < 0)) throw new Error("Bitte eine ganze Zahl ab 0 eingeben.");
        await speichern({ guestCount: n });
      }}
    >
      <label className={LABEL}>
        Gäste
        <Input name="gaeste" type="number" min={0} step={1} inputMode="numeric" autoFocus defaultValue={gaeste ?? ""} className="mt-1" />
      </label>
    </FensterFormular>
  );
}

export function DatumFenster({ datum, speichern, schliessen }: { datum: string | null; speichern: Speichern; schliessen: () => void }) {
  const [wert, setWert] = useState(datum?.slice(0, 10) ?? "");
  return (
    <FensterFormular schliessen={schliessen} speichern={async () => speichern({ eventDate: wert || null })}>
      <label className={LABEL}>
        Datum
        <DateInput aria-label="Datum" className="mt-1" defaultValue={wert} onValueChange={setWert} onCommit={setWert} />
      </label>
    </FensterFormular>
  );
}

/** Ort-Typ und Modus in EINER Auswahl — genau die drei Kategorien des Ort-Chips (lib/ort-typ.ts). */
const ORT_ART = [
  { value: "", label: "Nicht gepflegt" },
  { value: "in_house", label: "Im Haus · Event" },
  { value: "catering", label: "Außer Haus · Catering" },
  { value: "delivery", label: "Abholung" },
];

export function OrtFenster({
  ort,
  locationType,
  offSiteMode,
  speichern,
  schliessen,
}: {
  ort: string | null;
  locationType: "in_house" | "off_site" | null;
  offSiteMode: "delivery" | "catering" | null;
  speichern: Speichern;
  schliessen: () => void;
}) {
  const [art, setArt] = useState<string>(locationType === "in_house" ? "in_house" : locationType === "off_site" ? (offSiteMode ?? "") : "");
  return (
    <FensterFormular
      schliessen={schliessen}
      speichern={async (fd) => {
        const text = String(fd.get("ort") ?? "").trim();
        const typ = art === "in_house" ? "in_house" : art ? "off_site" : null;
        await speichern({ locationType: typ, offSiteMode: typ === "off_site" ? art : null, location: text || null });
      }}
    >
      <label className={LABEL}>
        Art
        <span className="mt-1 block">
          <Select ariaLabel="Ort-Art" value={art} onValueChange={setArt} options={ORT_ART} />
        </span>
      </label>
      <label className={LABEL}>
        {art === "in_house" ? "Raum / Bereich (optional)" : "Ort / Adresse"}
        <Input name="ort" data-testid="klein-fenster-ort" defaultValue={ort ?? ""} maxLength={200} className="mt-1" placeholder={art === "in_house" ? "z. B. Terrasse" : "Straße, Ort"} />
      </label>
    </FensterFormular>
  );
}

/** Ein einzelnes Textfeld (E-Mail, Telefon, Name). */
export function TextFenster({
  label,
  name,
  wert,
  type = "text",
  pflicht,
  maxLength,
  speichern,
  schliessen,
}: {
  label: string;
  name: string;
  wert: string | null;
  type?: "text" | "email" | "tel";
  pflicht?: boolean;
  maxLength?: number;
  speichern: Speichern;
  schliessen: () => void;
}) {
  return (
    <FensterFormular
      schliessen={schliessen}
      speichern={async (fd) => {
        const v = String(fd.get(name) ?? "").trim();
        if (pflicht && !v) throw new Error(`${label} darf nicht leer sein.`);
        await speichern({ [name]: v });
      }}
    >
      <label className={LABEL}>
        {label}
        <Input name={name} type={type} autoFocus defaultValue={wert ?? ""} maxLength={maxLength} className="mt-1" />
      </label>
    </FensterFormular>
  );
}

export function AdresseFenster({
  strasse,
  plz,
  stadt,
  speichern,
  schliessen,
}: {
  strasse: string | null;
  plz: string | null;
  stadt: string | null;
  speichern: Speichern;
  schliessen: () => void;
}) {
  return (
    <FensterFormular
      schliessen={schliessen}
      speichern={async (fd) => {
        const t = (k: string) => String(fd.get(k) ?? "").trim() || null;
        await speichern({ addressStreet: t("strasse"), addressZip: t("plz"), addressCity: t("stadt") });
      }}
    >
      <label className={LABEL}>
        Straße und Hausnummer
        <Input name="strasse" autoFocus defaultValue={strasse ?? ""} maxLength={200} className="mt-1" />
      </label>
      <div className="grid grid-cols-[90px_minmax(0,1fr)] gap-2">
        <label className={LABEL}>
          PLZ
          <Input name="plz" defaultValue={plz ?? ""} maxLength={32} className="mt-1" />
        </label>
        <label className={LABEL}>
          Ort
          <Input name="stadt" defaultValue={stadt ?? ""} maxLength={200} className="mt-1" />
        </label>
      </div>
    </FensterFormular>
  );
}
